import { getPrivateCollection, getSources } from '@/services/content';
import { getAllowedCollections } from '@/services/profile';
import { generateChatSessionId } from '@/utils/chat';

export const chatSlice = (set, get): ChatContextStore => ({
  isAddSourcesActive: false,
  clearState: false,
  newChat: true,
  chatSessionId: generateChatSessionId(),
  promptInput: '',
  chatAgent: 'Generic',
  nextBestActions: [],
  salesCallTranscript: undefined,
  context: '',
  contextName: '',
  sources: [],
  entryPoint: 'prompt',
  refreshSources: 0,
  includeSearch: true,
  allowedCollections: [],
  selectedSources: [],
  virtualCollectionList: [],
  activeChatHistoryRecord: undefined,
  collectionName: 'My Private Collection',
  collection: 'myContentsOnly',
  collectionType: 'private',
  collectionProductLine: '',
  isActiveChatHistoryLoading: false,
  isNewSession: false,
  sessionPath: '',
  backToChatSession: false,
  isRestrictedChatSessionStatus: '',
  isPrivateChat: false,
  isChatActive: false,
  isStreamingRequested: false,
  showScrollIcon: false,
  dynamicStreamCount: 0,
  uploadedFileTitle: '',
  showFileToasts: false,
  uploadedFile: null,
  wordsCount: 0,
  wordCountError: false,
  isStreamingStopped: false,
  localAutoScrollEnabledKey: '',
  localToolResultKey: '',
  availablePrivateDocs: [],
  isChatDisabled: false,
  historyCollectionName: '',
  isPromptSetManually: false,
  isToolsButtonSelected: false,
  dataAgentDocs: [],
  selectedCollectionType: 'my-documents',
  categoryCount: {},
  isRestrictedAppStatus: '',
  dragAndDropFiles: [],
  pendingMessageFiles: [] as string[],
  chatInputSources: [],
  chatInputSourceCategories: {},
  showGuidelinesModal: false,
  guidelinesAcknowledged: true,
  chatInputSourceTitles: {},
  setCategoryCount: (categoryCount) =>
    set((state) => ({
      categoryCount:
        typeof categoryCount === 'function'
          ? categoryCount(state.categoryCount ?? {})
          : categoryCount,
    })),
  setSelectedCollectionType: (selectedCollectionType) => set({ selectedCollectionType }),
  setIsAddSourcesActive: (isAddSourcesActive) => set({ isAddSourcesActive }),
  setClearState: (clearState) => set({ clearState }),
  setNewChat: (newChat) => set({ newChat }),
  setChatSessionId: (chatSessionId) => set({ chatSessionId }),
  setChatAgent: (chatAgent) => set({ chatAgent }),
  setPromptInput: (promptInput) => set({ promptInput }),
  setNextBestActions: (nextBestActions) => set({ nextBestActions }),
  setSalesCallTranscript: (salesCallTranscript) => set({ salesCallTranscript }),
  setContext: (context) => set({ context }),
  setContextName: (contextName) => set({ contextName }),
  setSources: (sources) =>
    set((state) => ({
      sources:
        typeof sources === 'function'
          ? (sources as (prev: string[] | undefined) => string[] | undefined)(state.sources)
          : sources,
    })),
  setEntryPoint: (entryPoint) => set({ entryPoint }),
  setRefreshSources: (refreshSources) => set({ refreshSources }),
  setIncludeSearch: (includeSearch) => set({ includeSearch }),
  setAllowedCollections: (allowedCollections) => set({ allowedCollections }),
  setSelectedSources: (selectedSources) =>
    set((state) => ({
      selectedSources:
        typeof selectedSources === 'function'
          ? (selectedSources as (prev: string[] | undefined) => string[] | undefined)(
              state.selectedSources
            )
          : selectedSources,
    })),
  setVirtualCollectionList: (virtualCollectionList) => set({ virtualCollectionList }),
  setActiveChatHistoryRecord: (activeChatHistoryRecord) => set({ activeChatHistoryRecord }),
  setCollectionName: (collectionName) => set({ collectionName }),
  setCollection: (collection) => set({ collection }),
  setCollectionType: (collectionType) => set({ collectionType }),
  setCollectionProductLine: (collectionProductLine) => set({ collectionProductLine }),
  setIsActiveChatHistoryLoading: (isActiveChatHistoryLoading) =>
    set({ isActiveChatHistoryLoading }),
  setIsNewSession: (isNewSession) => set({ isNewSession }),
  setSessionPath: (sessionPath) => set({ sessionPath }),
  setBackToChatSession: (backToChatSession) => set({ backToChatSession }),
  setIsRestrictedChatSessionStatus: (isRestrictedChatSessionStatus) =>
    set({ isRestrictedChatSessionStatus }),
  setIsPrivateChat: (isPrivateChat) => set({ isPrivateChat }),
  setIsChatActive: (isChatActive) => set({ isChatActive }),
  setIsStreamingRequested: (isStreamingRequested) => set({ isStreamingRequested }),
  setShowScrollIcon: (showScrollIcon) => set({ showScrollIcon }),
  setDynamicStreamCount: (dynamicStreamCount) => set({ dynamicStreamCount }),
  setUploadedFileTitle: (uploadedFileTitle) => set({ uploadedFileTitle }),
  setShowFileToasts: (showFileToasts) => set({ showFileToasts }),
  setUploadedFile: (uploadedFile) => set({ uploadedFile }),
  setWordsCount: (wordsCount) => set({ wordsCount }),
  setWordCountError: (wordCountError) => set({ wordCountError }),
  setIsStreamingStopped: (isStreamingStopped) => set({ isStreamingStopped }),
  setLocalAutoScrollEnabledKey: (localAutoScrollEnabledKey) => set({ localAutoScrollEnabledKey }),
  setLocalToolResultKey: (localToolResultKey) => set({ localToolResultKey }),
  setAvailablePrivateDocs: (availablePrivateDocs) => set({ availablePrivateDocs }),
  setIsChatDisabled: (isChatDisabled) => set({ isChatDisabled }),
  setHistoryCollectionName: (historyCollectionName) => set({ historyCollectionName }),
  setIsPromptSetManually: (isPromptSetManually) => set({ isPromptSetManually }),
  setIstoolsButtonSelected: (isToolsButtonSelected) => set({ isToolsButtonSelected }),
  setIsRestrictedAppStatus: (isRestrictedAppStatus) => set({ isRestrictedAppStatus }),
  setDataAgentDocs: (dataAgentDocs) => set({ dataAgentDocs }),
  setDragAndDropFiles: (dragAndDropFiles) => set({ dragAndDropFiles }),
  setPendingMessageFiles: (pendingMessageFiles: string[]) => set({ pendingMessageFiles }),
  setChatInputSources: (chatInputSources) =>
    set((state) => ({
      chatInputSources:
        typeof chatInputSources === 'function'
          ? (chatInputSources as (prev: string[] | undefined) => string[] | undefined)(
              state.chatInputSources
            )
          : chatInputSources,
    })),
  setChatInputSourceCategories: (chatInputSourceCategories) =>
    set((state) => ({
      chatInputSourceCategories:
        typeof chatInputSourceCategories === 'function'
          ? chatInputSourceCategories(state.chatInputSourceCategories ?? {})
          : chatInputSourceCategories,
    })),
  setChatInputSourceTitles: (chatInputSourceTitles) =>
    set((state) => ({
      chatInputSourceTitles:
        typeof chatInputSourceTitles === 'function'
          ? chatInputSourceTitles(state.chatInputSourceTitles ?? {})
          : chatInputSourceTitles,
    })),
  setShowGuidelinesModal: (open) => set({ showGuidelinesModal: open }),
  setGuidelinesAcknowledged: (value) => set({ guidelinesAcknowledged: value }),
  fetchAllowedCollections: async () => {
    const response = await getAllowedCollections();
    set(() => ({ allowedCollections: response }));
  },
  fetchDocs: async () => {
    const response = await getSources('true', '');
    set(() => ({ availablePrivateDocs: response?.data?.map((doc) => doc?.title) }));
    const uniqueTypes = await getPrivateCollection();
    const transformedList = uniqueTypes?.data?.map((type) => ({
      ckey: '',
      ctype: type,
      cline: '',
      cname: '',
      perm: 'manage',
    }));
    set(() => ({ virtualCollectionList: transformedList }));
  },
  chatInit: async () => {
    const { fetchAllowedCollections, fetchDocs } = get();
    await fetchAllowedCollections();
    await fetchDocs();
  },
});
